/**
 * PDF Generation Service using Gotenberg
 * 
 * This service sends HTML to Gotenberg server for PDF conversion
 * and stores the generated PDF in Supabase Storage.
 */

import { supabase } from '../api/supabase';

// Use proxy URL to avoid CORS issues in development
// In production, this should be updated to use the actual Gotenberg service URL
const GOTENBERG_URL = (() => {
  if (typeof window !== 'undefined') {
    // In browser - use webpack dev server proxy to avoid CORS
    // The proxy forwards /api/pdf/* to http://localhost:3001/*
    return '/api/pdf';
  }
  return 'http://localhost:3001';
})();

interface GeneratePDFOptions {
  html: string;
  fileName: string;
  storageBucket?: string;
  storagePath?: string;
}

interface PDFResult {
  success: boolean;
  pdfUrl?: string;
  error?: string;
  localBlob?: Blob;
}

/**
 * Generate PDF from HTML using Gotenberg
 */
export const generatePDFFromHTML = async (
  html: string,
  options?: {
    paperSize?: 'A4' | 'Letter';
    marginTop?: string;
    marginBottom?: string;
    marginLeft?: string;
    marginRight?: string;
    landscape?: boolean;
  }
): Promise<Blob> => {
  try {
    console.log('📄 Sending HTML to Gotenberg for PDF generation...');

    // Create form data for Gotenberg
    const formData = new FormData();
    
    // Add HTML file
    const htmlBlob = new Blob([html], { type: 'text/html' });
    formData.append('files', htmlBlob, 'index.html');

    // Add Gotenberg options
    if (options?.paperSize) {
      formData.append('paperWidth', options.paperSize === 'A4' ? '8.27' : '8.5');
      formData.append('paperHeight', options.paperSize === 'A4' ? '11.7' : '11');
    }
    if (options?.marginTop) formData.append('marginTop', options.marginTop);
    if (options?.marginBottom) formData.append('marginBottom', options.marginBottom);
    if (options?.marginLeft) formData.append('marginLeft', options.marginLeft);
    if (options?.marginRight) formData.append('marginRight', options.marginRight);
    if (options?.landscape) formData.append('landscape', 'true');

    // Send to Gotenberg
    const response = await fetch(`${GOTENBERG_URL}/forms/chromium/convert/html`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Gotenberg error: ${response.status} - ${errorText}`);
    }

    const pdfBlob = await response.blob();
    console.log('✅ PDF generated successfully:', pdfBlob.size, 'bytes');

    return pdfBlob;
  } catch (error) {
    console.error('❌ Failed to generate PDF:', error);
    throw error;
  }
};

/**
 * Generate PDF and store in Supabase Storage
 */
export const generateAndStorePDF = async ({
  html,
  fileName,
  storageBucket = 'delivery-forms',
  storagePath = 'pdfs',
}: GeneratePDFOptions): Promise<PDFResult> => {
  try {
    // Generate PDF from HTML
    const pdfBlob = await generatePDFFromHTML(html, {
      paperSize: 'A4',
      marginTop: '0.5in',
      marginBottom: '0.5in',
      marginLeft: '0.5in',
      marginRight: '0.5in',
    });

    // Ensure fileName has .pdf extension
    const sanitizedFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    const fullPath = `${storagePath}/${sanitizedFileName}`;

    console.log('📤 Uploading PDF to Supabase Storage:', fullPath);

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(storageBucket)
      .upload(fullPath, pdfBlob, {
        contentType: 'application/pdf',
        upsert: true, // Replace if exists
      });

    if (error) {
      console.error('❌ Failed to upload PDF to Supabase:', error);
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(storageBucket)
      .getPublicUrl(fullPath);

    console.log('✅ PDF stored successfully:', urlData.publicUrl);

    return {
      success: true,
      pdfUrl: urlData.publicUrl,
      localBlob: pdfBlob,
    };
  } catch (error) {
    console.error('❌ PDF generation and storage failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Generate PDF and download directly (no storage)
 */
export const generateAndDownloadPDF = async (
  html: string,
  fileName: string
): Promise<void> => {
  try {
    const pdfBlob = await generatePDFFromHTML(html, {
      paperSize: 'A4',
      marginTop: '0.5in',
      marginBottom: '0.5in',
      marginLeft: '0.5in',
      marginRight: '0.5in',
    });

    // Create download link
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('✅ PDF downloaded successfully');
  } catch (error) {
    console.error('❌ PDF download failed:', error);
    throw error;
  }
};

/**
 * Check if Gotenberg service is available
 */
export const checkGotenbergAvailability = async (): Promise<boolean> => {
  try {
    console.log('🔍 Checking Gotenberg availability at:', GOTENBERG_URL);
    const response = await fetch(`${GOTENBERG_URL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    const isAvailable = response.ok;
    console.log(isAvailable ? '✅ Gotenberg is available!' : '❌ Gotenberg returned error status');
    return isAvailable;
  } catch (error) {
    console.warn('⚠️ Gotenberg service not available:', error);
    console.log('💡 Make sure Docker is running: docker ps');
    return false;
  }
};
